import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { Star, MapPin, Clock, Phone, ArrowLeft } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=1200";

export default function TruckProfile() {
  const { id } = useParams();
  const [t, setT] = useState(null);

  useEffect(() => { api.get(`/trucks/${id}`).then((r) => setT(r.data)).catch(() => {}); }, [id]);

  if (!t) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-4 border-teal-200 border-t-[#0FB5A6] animate-spin" /></div>;

  const pin = L.divIcon({ className: "gr-pin", html: `<div class="gr-pin-inner gr-pin-live">${t.name.charAt(0)}</div>`, iconSize: [40, 40], iconAnchor: [20, 20] });

  return (
    <div>
      <div className="relative h-[38vh] min-h-[280px]">
        <img src={t.cover_url || FALLBACK} alt={t.name} className="w-full h-full object-cover" onError={(e) => (e.currentTarget.src = FALLBACK)} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
        <Link to="/explore" className="absolute top-5 left-5 gr-glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-1" data-testid="back-to-map"><ArrowLeft className="w-4 h-4" /> Map</Link>
        <div className="absolute bottom-6 left-0 right-0 max-w-6xl mx-auto px-5">
          <div className="flex items-center gap-2 mb-2">
            {t.is_live ? <span className="px-3 py-1 rounded-full bg-[#0FB5A6] text-white text-xs font-bold">● Live now</span> : <span className="px-3 py-1 rounded-full bg-slate-800/80 text-white text-xs font-bold">Offline</span>}
            {t.tier !== "basic" && <span className="px-3 py-1 rounded-full bg-[#F2635F] text-slate-900 text-xs font-bold capitalize">{t.tier}</span>}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">{t.name}</h1>
          <p className="text-white/80 font-bold uppercase tracking-widest text-xs mt-2">{t.cuisine} · {t.city}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-teal-100 p-6">
            <h2 className="font-display text-xl font-bold mb-2">About</h2>
            <p className="text-slate-600 leading-relaxed">{t.description || "No description yet."}</p>
            <div className="flex flex-wrap gap-5 mt-5 text-sm text-slate-600">
              <span className="flex items-center gap-2"><Star className="w-4 h-4 fill-[#F2635F] text-[#F2635F]" /> {(t.rating || 4.6).toFixed(1)} rating</span>
              {t.hours && <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> {t.hours}</span>}
              {t.phone && <span className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> {t.phone}</span>}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-teal-100 p-6">
            <h2 className="font-display text-xl font-bold mb-4">Menu</h2>
            {t.menu?.length ? (
              <div className="grid sm:grid-cols-2 gap-3">
                {t.menu.map((m, i) => (
                  <div key={i} className="flex justify-between gap-3 p-4 rounded-xl bg-teal-50/50 border border-teal-100">
                    <div>
                      <p className="font-bold">{m.name}</p>
                      {m.description && <p className="text-sm text-slate-500">{m.description}</p>}
                    </div>
                    <span className="font-display font-bold text-[#0FB5A6]">${Number(m.price).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-400">Menu coming soon.</p>}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-teal-100 p-6">
            <h2 className="font-display text-xl font-bold mb-1">Where to find it</h2>
            <p className="text-sm text-slate-500 mb-4 flex items-center gap-1"><MapPin className="w-4 h-4 text-[#0FB5A6]" /> {t.location_note || t.city}</p>
            {t.lat != null ? (
              <div className="h-56 rounded-xl overflow-hidden">
                <MapContainer center={[t.lat, t.lng]} zoom={14} className="w-full h-full" scrollWheelZoom={false}>
                  <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
                  <Marker position={[t.lat, t.lng]} icon={pin} />
                </MapContainer>
              </div>
            ) : <p className="text-slate-400 text-sm">This truck hasn't shared a live location yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
